/**
 * customer-decision.ts - the Kundenfreigabe + Kundenentscheid + Promotion contract
 * (P2 Placement Slice 12, Ausschreibungs-Screen Tab 4c + Kundenportal «Offerten» / «Einwilligungen»,
 * Prozessschritte 13 + 14; Detailkonzept Ausschreibung V3.0 Ch 6.4 / 6.5 / 8.7 / 9 / 11, P2 Section 7
 * steps 13 + 14, Section 8 E5/E13/E14, Section 10.1/10.2, Ue8, Section 12 UC12b/UC13/UC14).
 *
 * The SINGLE SOURCE OF TRUTH for:
 *   - the delivery lifecycle (vorbereitet -> zugestellt -> entschieden) + channel/channel-quelle vocab,
 *   - the Kundenentscheid vocab (angenommen / abgelehnt / rueckfrage) + Entscheider-Art,
 *   - the Kundenfreigabe Sperrgrund + the pure gate helper,
 *   - the Kanal mapping (briefpost -> email + hint) + the E5 deviation helper,
 *   - the wire shapes (broker Tab 4c + portal «Offerten» + portal «Einwilligungen»).
 *
 * Pure module - no I/O, no formatters, no Date-TZ parse. Every user-facing literal uses ASCII hyphens
 * (no-restricted-syntax forbids en/em dashes in packages/shared/src/**).
 *
 * [Source: docs/product/p2-slices/slice-12-kundenfreigabe-promotion-design.md Section 3]
 * [Source: packages/shared/src/offer.ts] (triad + wire idioms)
 */

import type { PlacementStatus, PlacementClosureStatus } from './placement';
import type { ChannelKey, DeliveryDocType } from './channel';
import type { VergleichAmpel, VergleichZeile, VeraltetGrund } from './offer-comparison';
import { RISIKOPROFIL_CONSENT_PURPOSE } from './kunde-risikoprofil';

// -- Vocabularies (triad idiom) ------------------------------------------------

/** The delivery lifecycle (one Zustellungsfall per released report). */
export const CUSTOMER_DELIVERY_STATUS = {
  VORBEREITET: 'vorbereitet',
  ZUGESTELLT: 'zugestellt',
  ENTSCHIEDEN: 'entschieden',
} as const;
export type CustomerDeliveryStatus =
  (typeof CUSTOMER_DELIVERY_STATUS)[keyof typeof CUSTOMER_DELIVERY_STATUS];
export const CUSTOMER_DELIVERY_STATUS_VALUES: readonly CustomerDeliveryStatus[] = [
  CUSTOMER_DELIVERY_STATUS.VORBEREITET,
  CUSTOMER_DELIVERY_STATUS.ZUGESTELLT,
  CUSTOMER_DELIVERY_STATUS.ENTSCHIEDEN,
];
export const CUSTOMER_DELIVERY_STATUS_LABELS: Record<CustomerDeliveryStatus, string> = {
  [CUSTOMER_DELIVERY_STATUS.VORBEREITET]: 'Versand vorbereitet',
  [CUSTOMER_DELIVERY_STATUS.ZUGESTELLT]: 'Beim Kunden',
  [CUSTOMER_DELIVERY_STATUS.ENTSCHIEDEN]: 'Entschieden',
};

/** The delivery channel (portal durable delivery or the broker mailbox). */
export const CUSTOMER_DELIVERY_CHANNEL = {
  PORTAL: 'portal',
  EMAIL: 'email',
} as const;
export type CustomerDeliveryChannel =
  (typeof CUSTOMER_DELIVERY_CHANNEL)[keyof typeof CUSTOMER_DELIVERY_CHANNEL];
export const CUSTOMER_DELIVERY_CHANNEL_VALUES: readonly CustomerDeliveryChannel[] = [
  CUSTOMER_DELIVERY_CHANNEL.PORTAL,
  CUSTOMER_DELIVERY_CHANNEL.EMAIL,
];
export const CUSTOMER_DELIVERY_CHANNEL_LABELS: Record<CustomerDeliveryChannel, string> = {
  [CUSTOMER_DELIVERY_CHANNEL.PORTAL]: 'Kundenportal',
  [CUSTOMER_DELIVERY_CHANNEL.EMAIL]: 'E-Mail',
};

/** How the current channel was chosen. */
export const CUSTOMER_DELIVERY_CHANNEL_QUELLE = {
  KANALSTEUERUNG: 'kanalsteuerung',
  BROKER: 'broker',
} as const;
export type CustomerDeliveryChannelQuelle =
  (typeof CUSTOMER_DELIVERY_CHANNEL_QUELLE)[keyof typeof CUSTOMER_DELIVERY_CHANNEL_QUELLE];
export const CUSTOMER_DELIVERY_CHANNEL_QUELLE_VALUES: readonly CustomerDeliveryChannelQuelle[] = [
  CUSTOMER_DELIVERY_CHANNEL_QUELLE.KANALSTEUERUNG,
  CUSTOMER_DELIVERY_CHANNEL_QUELLE.BROKER,
];
export const CUSTOMER_DELIVERY_CHANNEL_QUELLE_LABELS: Record<CustomerDeliveryChannelQuelle, string> = {
  [CUSTOMER_DELIVERY_CHANNEL_QUELLE.KANALSTEUERUNG]: 'gemaess Kanalsteuerung',
  [CUSTOMER_DELIVERY_CHANNEL_QUELLE.BROKER]: 'vom Broker gewaehlt',
};

/** The append-only delivery-log row kinds (every send + every channel change). */
export const CUSTOMER_DELIVERY_LOG_ART = {
  VERSAND: 'versand',
  KANALWECHSEL: 'kanalwechsel',
  ERNEUT_VERSANDT: 'erneut_versandt',
} as const;
export type CustomerDeliveryLogArt =
  (typeof CUSTOMER_DELIVERY_LOG_ART)[keyof typeof CUSTOMER_DELIVERY_LOG_ART];
export const CUSTOMER_DELIVERY_LOG_ART_VALUES: readonly CustomerDeliveryLogArt[] = [
  CUSTOMER_DELIVERY_LOG_ART.VERSAND,
  CUSTOMER_DELIVERY_LOG_ART.KANALWECHSEL,
  CUSTOMER_DELIVERY_LOG_ART.ERNEUT_VERSANDT,
];
export const CUSTOMER_DELIVERY_LOG_ART_LABELS: Record<CustomerDeliveryLogArt, string> = {
  [CUSTOMER_DELIVERY_LOG_ART.VERSAND]: 'Zugestellt',
  [CUSTOMER_DELIVERY_LOG_ART.KANALWECHSEL]: 'Kanal gewechselt',
  [CUSTOMER_DELIVERY_LOG_ART.ERNEUT_VERSANDT]: 'Erneut zugestellt',
};

/** The channel the customer used to respond (Ch 6.5 customer_response_channel). */
export const CUSTOMER_RESPONSE_CHANNEL = {
  PORTAL: 'portal',
  EMAIL: 'email',
  TELEFON: 'telefon',
  PERSOENLICH: 'persoenlich',
} as const;
export type CustomerResponseChannel =
  (typeof CUSTOMER_RESPONSE_CHANNEL)[keyof typeof CUSTOMER_RESPONSE_CHANNEL];
export const CUSTOMER_RESPONSE_CHANNEL_VALUES: readonly CustomerResponseChannel[] = [
  CUSTOMER_RESPONSE_CHANNEL.PORTAL,
  CUSTOMER_RESPONSE_CHANNEL.EMAIL,
  CUSTOMER_RESPONSE_CHANNEL.TELEFON,
  CUSTOMER_RESPONSE_CHANNEL.PERSOENLICH,
];
export const CUSTOMER_RESPONSE_CHANNEL_LABELS: Record<CustomerResponseChannel, string> = {
  [CUSTOMER_RESPONSE_CHANNEL.PORTAL]: 'Portal-Bestaetigung',
  [CUSTOMER_RESPONSE_CHANNEL.EMAIL]: 'E-Mail-Antwort',
  [CUSTOMER_RESPONSE_CHANNEL.TELEFON]: 'Telefon',
  [CUSTOMER_RESPONSE_CHANNEL.PERSOENLICH]: 'Persoenlich',
};

/** The Kundenentscheid event kinds. */
export const PLACEMENT_DECISION_ART = {
  ANGENOMMEN: 'angenommen',
  ABGELEHNT: 'abgelehnt',
  RUECKFRAGE: 'rueckfrage',
} as const;
export type PlacementDecisionArt =
  (typeof PLACEMENT_DECISION_ART)[keyof typeof PLACEMENT_DECISION_ART];
export const PLACEMENT_DECISION_ART_VALUES: readonly PlacementDecisionArt[] = [
  PLACEMENT_DECISION_ART.ANGENOMMEN,
  PLACEMENT_DECISION_ART.ABGELEHNT,
  PLACEMENT_DECISION_ART.RUECKFRAGE,
];
export const PLACEMENT_DECISION_ART_LABELS: Record<PlacementDecisionArt, string> = {
  [PLACEMENT_DECISION_ART.ANGENOMMEN]: 'Offerte angenommen',
  [PLACEMENT_DECISION_ART.ABGELEHNT]: 'Alle Offerten abgelehnt',
  [PLACEMENT_DECISION_ART.RUECKFRAGE]: 'Rueckfrage',
};

/** Who recorded the decision. */
export const ENTSCHEIDER_ART = {
  KUNDE_PORTAL: 'kunde_portal',
  BROKER_ERFASST: 'broker_erfasst',
} as const;
export type EntscheiderArt = (typeof ENTSCHEIDER_ART)[keyof typeof ENTSCHEIDER_ART];
export const ENTSCHEIDER_ART_VALUES: readonly EntscheiderArt[] = [
  ENTSCHEIDER_ART.KUNDE_PORTAL,
  ENTSCHEIDER_ART.BROKER_ERFASST,
];
export const ENTSCHEIDER_ART_LABELS: Record<EntscheiderArt, string> = {
  [ENTSCHEIDER_ART.KUNDE_PORTAL]: 'Kunde im Portal',
  [ENTSCHEIDER_ART.BROKER_ERFASST]: 'vom Broker erfasst',
};

/** Why «Dem Kunden zustellen» is blocked. */
export type KundenfreigabeSperrgrund =
  | 'placement_locked'
  | 'placement_status_invalid'
  | 'recommendation_not_released'
  | 'report_veraltet'
  | 'docx_fehlt'
  | 'docx_veraltet'
  | 'zustellung_offen'
  | 'bereits_entschieden';

// -- Status machine ------------------------------------------------------------

export const CUSTOMER_DELIVERY_STATUS_TRANSITIONS: Record<
  CustomerDeliveryStatus,
  readonly CustomerDeliveryStatus[]
> = {
  vorbereitet: ['zugestellt'],
  zugestellt: ['entschieden'],
  entschieden: [],
};

/** Is the `to` delivery status a legal next step from `from`? */
export function canTransitionCustomerDelivery(
  from: CustomerDeliveryStatus,
  to: CustomerDeliveryStatus,
): boolean {
  return CUSTOMER_DELIVERY_STATUS_TRANSITIONS[from].includes(to);
}

// -- Gates / constants ---------------------------------------------------------

/** Kundenfreigabe (step 13) is only reachable once the placement is empfohlen. */
export const KUNDENFREIGABE_ERLAUBTE_STATUS: readonly PlacementStatus[] = ['empfohlen'];
/** Kundenentscheid (step 14) is only recordable while the placement is empfohlen. */
export const KUNDENENTSCHEID_ERLAUBTE_STATUS: readonly PlacementStatus[] = ['empfohlen'];
/** The e-mail send lock window (Slice 8 posture). */
export const KUNDENFREIGABE_VERSAND_LOCK_MINUTES = 10;
/** The Dokumenttyp the Kanalsteuerung resolves the Vergleichsdokument against (Q103). */
export const KUNDENFREIGABE_DELIVERY_DOC_TYPE: DeliveryDocType = 'korrespondenz';
/** Minimum length of the Kanalwechsel-Grund. */
export const KUNDENFREIGABE_GRUND_MIN = 3;
/** Minimum length of the E5 deviation reason. */
export const ABWEICHUNG_GRUND_MIN = 3;
/** Minimum length of the Rueckfrage text. */
export const RUECKFRAGE_TEXT_MIN = 3;
/** Maximum length of the Entscheider name. */
export const ENTSCHEIDER_NAME_MAX = 200;

/** The one consent purpose the portal «Einwilligungen» self-service manages (Slice 5 gating dependency). */
export const PORTAL_CONSENT_PURPOSES: readonly string[] = [RISIKOPROFIL_CONSENT_PURPOSE];

/** The fixed German wording per consent purpose (Q111; revDSG Art. 5 Bst. c). */
export const PORTAL_CONSENT_PURPOSE_LABELS: Record<string, { label: string; beschreibung: string }> = {
  [RISIKOPROFIL_CONSENT_PURPOSE]: {
    label: 'Risikoprofil - besondere Personendaten',
    beschreibung:
      'Ich willige ein, dass mein Broker den Status der Gesundheitspruefung und meine Einkommens-Bandbreite fuer die Risikoanalyse und Ausschreibungen verwendet (revDSG Art. 5 Bst. c). Die Einwilligung kann jederzeit widerrufen werden; der Widerruf wirkt ab sofort.',
  },
};

// -- Texts (ASCII hyphens only) ------------------------------------------------

export const KUNDENFREIGABE_PORTAL_TITLE = (nr: string): string =>
  `Offertvergleich ${nr} - Empfehlung zur Freigabe`;
export const KUNDENFREIGABE_BRIEFPOST_HINWEIS =
  'Briefpost wird nicht unterstuetzt - E-Mail vorgeschlagen';
export const KUNDENFREIGABE_KEIN_PORTAL_HINWEIS =
  'Der Kunde hat keinen aktiven Portalzugang - Zustellung per E-Mail oder zuerst einladen.';
export const KUNDENFREIGABE_KEINE_EMAIL_HINWEIS =
  'Beim Kunden ist keine E-Mail-Adresse hinterlegt.';
export const KUNDENFREIGABE_NICHT_FREIGEGEBEN_HINWEIS =
  'Die Empfehlung ist noch nicht freigegeben - zuerst in Schritt 4b freigeben.';
export const KUNDENFREIGABE_VERALTET_HINWEIS =
  'Der freigegebene Vergleich ist nicht mehr aktuell - Vergleich erneut ausfuehren und freigeben.';
export const KUNDENFREIGABE_OFFEN_HINWEIS =
  'Die Kundenfreigabe ist zugestellt - der Entscheid des Kunden steht aus.';
export const KUNDENFREIGABE_FIDLEG_HINWEIS =
  'Der Kundenentscheid wird inkl. Beratungsdokumentation (Vergleichsdokument, Empfehlungsbegruendung) revisionssicher protokolliert (FIDLEG).';
export const KUNDENENTSCHEID_ABWEICHEND_LABEL = 'Abweichend von der Empfehlung';
export const KUNDENENTSCHEID_ABWEICHUNG_HINWEIS =
  'Bitte begruenden, weshalb eine andere als die empfohlene Offerte gewaehlt wird.';
export const KUNDENENTSCHEID_E13_HINWEIS =
  'Alle Offerten abgelehnt - die Ausschreibung geht zurueck zur Versichererauswahl (Schritt 5), bestehende Offerten bleiben erhalten.';
export const PROMOTION_HINWEIS =
  'Mit der Annahme werden Risikoprofil und Soll-Deckungsvorgaben dieser Ausschreibung auf die Police uebernommen.';
export const PROMOTION_NEUE_POLICE_HINWEIS =
  'Neugeschaeft - mit der Annahme wird eine neue Police (Entwurf) angelegt.';
export const PORTAL_OFFERTEN_LEER_HINWEIS =
  'Zurzeit liegen keine Offertvergleiche zur Freigabe vor.';
export const PORTAL_OFFERTEN_ANNEHMEN_BESTAETIGUNG =
  'Ich habe das Vergleichsdokument und die Empfehlung gelesen und waehle diese Offerte verbindlich.';
export const PORTAL_OFFERTEN_ENTSCHIEDEN_HINWEIS =
  'Ihr Entscheid wurde an Ihren Broker uebermittelt.';
export const PORTAL_EINWILLIGUNG_NUR_HAUPTINHABER_HINWEIS =
  'Einwilligungen koennen nur vom Hauptinhaber des Portalzugangs erteilt oder widerrufen werden.';

// -- Pure helpers --------------------------------------------------------------

/**
 * The single Kundenfreigabe gate (D2). Order: closure -> status -> entschieden -> released ->
 * veraltet -> docx present -> docx for the released status -> offene Zustellung. Returns null when
 * delivery is allowed. `docxVorhanden`/`docxFreigegeben` default to true so callers that only pass
 * the placement/release facts keep the historical behaviour; the delivery path sets them from the
 * released report (a released comparison must carry a `freigegeben`-status .docx = the FIDLEG
 * Beratungsnachweis, review M4).
 */
export function kundenfreigabeSperrgrund(input: {
  status: PlacementStatus;
  closure: PlacementClosureStatus;
  released: boolean;
  veraltet: boolean;
  offeneZustellung: boolean;
  entschieden: boolean;
  docxVorhanden?: boolean;
  docxFreigegeben?: boolean;
}): KundenfreigabeSperrgrund | null {
  if (input.closure !== 'offen') return 'placement_locked';
  if (!KUNDENFREIGABE_ERLAUBTE_STATUS.includes(input.status)) return 'placement_status_invalid';
  if (input.entschieden) return 'bereits_entschieden';
  if (!input.released) return 'recommendation_not_released';
  if (input.veraltet) return 'report_veraltet';
  if (input.docxVorhanden === false) return 'docx_fehlt';
  if (input.docxFreigegeben === false) return 'docx_veraltet';
  if (input.offeneZustellung) return 'zustellung_offen';
  return null;
}

/** Map the Kanalsteuerung result onto a delivery channel (briefpost -> email + hint). */
export function mapVersandKanal(resolved: ChannelKey): {
  channel: CustomerDeliveryChannel;
  hinweis: string | null;
} {
  if (resolved === 'portal') return { channel: 'portal', hinweis: null };
  if (resolved === 'briefpost') return { channel: 'email', hinweis: KUNDENFREIGABE_BRIEFPOST_HINWEIS };
  return { channel: 'email', hinweis: null };
}

/** E5: the chosen offer deviates from the frozen recommendation. */
export function istAbweichend(gewaehlteOfferId: string, empfehlungOfferId: string): boolean {
  return gewaehlteOfferId !== empfehlungOfferId;
}

/** Whether a decision may still be recorded against this delivery. */
export function entscheidErlaubt(delivery: {
  status: CustomerDeliveryStatus;
  is_aktuell: boolean;
}): 'ok' | 'bereits_entschieden' | 'zustellung_ersetzt' | 'nicht_zugestellt' {
  if (delivery.status === 'entschieden') return 'bereits_entschieden';
  if (delivery.status !== 'zugestellt') return 'nicht_zugestellt';
  if (!delivery.is_aktuell) return 'zustellung_ersetzt';
  return 'ok';
}

// -- Events (Ch 11 verbatim + Slice-12 additions) ------------------------------

export const CUSTOMER_DECISION_EVENTS = {
  DELIVERED: 'comparison.delivered_to_customer',
  REDELIVERED: 'comparison.redelivered_to_customer',
  CHANNEL_CHANGED: 'customer.delivery_channel_changed',
  DECISION_RECORDED: 'placement.decision.recorded',
  RUECKFRAGE: 'placement.decision.rueckfrage',
  ALLE_ABGELEHNT: 'placement.decision.alle_abgelehnt',
  PROMOTED: 'placement.promoted_to_policy',
  CONSENT_SET: 'customer.consent_set',
} as const;
export type CustomerDecisionEvent =
  (typeof CUSTOMER_DECISION_EVENTS)[keyof typeof CUSTOMER_DECISION_EVENTS];

// -- Wire types (broker Tab 4c) ------------------------------------------------

export interface CustomerDeliveryLogEintrag {
  id: string;
  art: CustomerDeliveryLogArt;
  art_label: string;
  channel: CustomerDeliveryChannel;
  channel_von: CustomerDeliveryChannel | null;
  channel_nach: CustomerDeliveryChannel | null;
  grund: string | null;
  empfaenger: string | null;
  created_by_name: string | null;
  created_at: string;
}

export interface CustomerDeliveryEintrag {
  id: string;
  report_id: string;
  lauf_nr: number;
  status: CustomerDeliveryStatus;
  status_label: string;
  is_aktuell: boolean;
  channel: CustomerDeliveryChannel;
  channel_label: string;
  channel_quelle: CustomerDeliveryChannelQuelle;
  channel_changed_at: string | null;
  channel_changed_by_name: string | null;
  kanalwechsel_count: number;
  empfehlung_offer_id: string;
  empfehlung_carrier_name: string;
  empfehlung_submission_number: string;
  docx: { filename: string; sha256: string; datei_url: string };
  sent_at: string | null;
  last_sent_at: string | null;
  sent_by_name: string | null;
  versand_count: number;
  versand_laeuft: boolean;
  /** PR #43 wave 2 (#5): a record_failed marker - every send on this delivery is blocked until resolved. */
  versand_gesperrt: boolean;
  /** PR #43 wave 2 (#5): the stored comm id makes `nachtragen` possible. */
  nachtragen_moeglich: boolean;
  letzter_versandfehler: string | null;
  letzter_versandfehler_at: string | null;
  customer_response_channel: CustomerResponseChannel | null;
  approved_at: string | null;
  approved_offer_id: string | null;
  hinweis: string | null;
  log: CustomerDeliveryLogEintrag[];
  created_at: string;
  updated_at: string;
}

export interface PlacementDecisionEintrag {
  id: string;
  customer_delivery_id: string;
  report_id: string;
  art: PlacementDecisionArt;
  art_label: string;
  decided_at: string;
  entscheider_art: EntscheiderArt;
  entscheider_name: string | null;
  erfasst_by_name: string | null;
  response_channel: CustomerResponseChannel;
  response_channel_label: string;
  gewaehlte_offer_id: string | null;
  gewaehlte_submission_number: string | null;
  gewaehlte_carrier_name: string | null;
  gewaehlte_version_no: number | null;
  empfehlung_offer_id: string;
  abweichend: boolean;
  abweichung_grund: string | null;
  praemie_jahr: number | null;
  versicherungsbeginn: string | null;
  text: string | null;
  report_veraltet_bei_entscheid: boolean;
  promotion: {
    promoted_at: string;
    policy_id: string;
    policy_neu: boolean;
    policy_number: string | null;
    objekte: number;
    regeln: number;
    reclassified: boolean;
  } | null;
}

export interface KundenfreigabeResponse {
  placement_id: string;
  placement_number: string;
  status: PlacementStatus;
  closure_status: PlacementClosureStatus;
  branchencode: string;
  freigabe: {
    report_id: string;
    lauf_nr: number;
    freigegeben_at: string;
    freigegeben_by_name: string | null;
    empfehlung_offer_id: string;
    empfehlung_carrier_name: string;
    empfehlung_submission_number: string;
    veraltet: VeraltetGrund | null;
    docx_vorhanden: boolean;
  } | null;
  zustellung: CustomerDeliveryEintrag | null;
  entscheide: PlacementDecisionEintrag[];
  zustellen_erlaubt: boolean;
  sperrgrund: KundenfreigabeSperrgrund | null;
  kanal_vorschlag: CustomerDeliveryChannel;
  kanal_quelle: 'kanalsteuerung';
  kanal_hinweis: string | null;
  portal_verfuegbar: boolean;
  kunde_email: string | null;
  kunde_name: string;
  kanalwechsel_erlaubt: boolean;
  erneut_senden_erlaubt: boolean;
  entscheid_erfassen_erlaubt: boolean;
  offerten: Array<{
    offer_document_id: string;
    submission_number: string;
    carrier_name: string;
    rang: number;
    score_total: number;
    praemie_jahr: number | null;
    version_no: number;
    ist_empfehlung: boolean;
  }>;
}

export interface KundenfreigabeZustellenBody {
  channel?: CustomerDeliveryChannel;
  hinweis?: string | null;
}
export interface KundenfreigabeKanalBody {
  channel: CustomerDeliveryChannel;
  grund: string;
}
export interface KundenentscheidErfassenBody {
  art: PlacementDecisionArt;
  gewaehlte_offer_id?: string;
  abweichung_grund?: string | null;
  praemie_jahr?: number | null;
  versicherungsbeginn?: string | null;
  response_channel: Exclude<CustomerResponseChannel, 'portal'>;
  entscheider_name: string;
  text?: string | null;
}

// -- Portal wire types (NO tenant/user ids, NO carrier e-mails, NO broker-internal flags) ------

export interface PortalOffertListItem {
  delivery_id: string;
  placement_number: string;
  branchen_label: string;
  lauf_nr: number;
  status: CustomerDeliveryStatus;
  status_label: string;
  zugestellt_am: string;
  empfehlung_carrier_name: string;
  anzahl_offerten: number;
  entschieden_am: string | null;
  entscheid_art: PlacementDecisionArt | null;
  rueckfrage_offen: boolean;
}

export interface PortalOffertAngebot {
  offer_document_id: string;
  carrier_name: string;
  logo_url: string | null;
  rang: number;
  score_total: number;
  ampel: VergleichAmpel;
  ist_empfehlung: boolean;
  praemie_jahr: number | null;
  versicherungssumme: number | null;
  selbstbehalt: number | null;
  vertragsdauer_monate: number | null;
  ausschluesse: string[];
  staerken: string[];
  schwaechen: string[];
  e4_flag: boolean;
}

export interface PortalOffertDeckungsziel {
  key: string;
  label: string;
  soll_text: string;
  mindestanforderung: boolean;
  zellen: Record<string, { ist_text: string; ampel: VergleichAmpel }>;
}

export interface PortalOffertDetail {
  delivery_id: string;
  placement_number: string;
  branchen_label: string;
  lauf_nr: number;
  status: CustomerDeliveryStatus;
  status_label: string;
  zugestellt_am: string;
  channel: CustomerDeliveryChannel;
  broker_name: string;
  berater_name: string | null;
  empfehlung: {
    offer_document_id: string;
    carrier_name: string;
    begruendung: string;
    zusammenfassung: string | null;
    abweichend_von_score: boolean;
  };
  deckungsziele: PortalOffertDeckungsziel[];
  matrix: VergleichZeile[];
  angebote: PortalOffertAngebot[];
  dokument: { filename: string; datei_url: string };
  entscheid: {
    art: PlacementDecisionArt;
    decided_at: string;
    gewaehlte_offer_id: string | null;
    gewaehlte_carrier_name: string | null;
    abweichend: boolean;
    text: string | null;
    promotion_policy_neu: boolean | null;
  } | null;
  rueckfragen: Array<{ id: string; decided_at: string; text: string }>;
  aktionen: { annehmen: boolean; ablehnen: boolean; rueckfrage: boolean };
  hinweise: string[];
}

export interface PortalOffertAnnehmenBody {
  offer_document_id: string;
  abweichung_grund?: string | null;
  bestaetigung: true;
}
export interface PortalOffertAblehnenBody {
  grund: string;
}
export interface PortalOffertRueckfrageBody {
  text: string;
}

export interface PortalEinwilligungItem {
  purpose: string;
  label: string;
  beschreibung: string;
  erteilt: boolean;
  erteilt_am: string | null;
  widerrufen_am: string | null;
  aenderbar: boolean;
}
export interface PortalEinwilligungenResponse {
  items: PortalEinwilligungItem[];
  hinweis: string | null;
}
export interface PortalEinwilligungPutBody {
  erteilt: boolean;
}
